import { useApolloClient } from "@apollo/client/react";
import { GQL, Utils } from "@binale-tech/shared";
import { Button } from "@heroui/react";
import React, { type FC, memo, use, useCallback, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";

import { type AiLogSource, fetchAiLogSource } from "@dms/components/Bewirtung/aiLogSource";
import { BewirtungForm } from "@dms/components/Bewirtung/BewirtungForm";
import { type AssistantActions } from "@dms/components/DmsSortAssistant/useAssistantActions";
import { UserContext } from "@domain/context/UserProvider";

import { ANSWER_CLASS, AnswerButton, LaterButton, QUESTION_CLASS, type Run } from "./panelShared";

type Props = {
    document: GQL.IDocument;
    isPending: boolean;
    run: Run;
    onAdvance: () => void;
    onLater?: () => void;
    saveBewirtung: AssistantActions["saveBewirtung"];
};

type Picked = { kind: GQL.IDmsBewirtungKind; source: AiLogSource; failed: boolean };

const BEWIRTUNG_KINDS: { kind: GQL.IDmsBewirtungKind; labelId: string }[] = [
    { kind: GQL.IDmsBewirtungKind.Business, labelId: "app.dms.bewirtung.kind.business" },
    { kind: GQL.IDmsBewirtungKind.EmployeesOnly, labelId: "app.dms.bewirtung.kind.employees" },
];

const BewirtungPanelImpl: FC<Props> = ({ document, isPending, run, onAdvance, onLater, saveBewirtung }) => {
    const client = useApolloClient();
    const { fireUser } = use(UserContext);
    const [picked, setPicked] = useState<Picked | null>(null);
    const [isFetching, setIsFetching] = useState(false);
    const hostName = fireUser?.displayName || fireUser?.email || "";

    const handleKind = useCallback(
        async (kind: GQL.IDmsBewirtungKind) => {
            setIsFetching(true);
            try {
                const { source, failed } = await fetchAiLogSource(client, document.key, document.companyId);
                setPicked({ kind, source, failed });
            } finally {
                setIsFetching(false);
            }
        },
        [client, document.companyId, document.key],
    );

    // BewirtungForm resets on every new initialValue identity, so build it once per pick.
    const prefill = useMemo(
        () =>
            picked
                ? { ...Utils.BewirtungPrefill.buildBewirtungPrefill(picked.source, hostName), kind: picked.kind }
                : null,
        [hostName, picked],
    );

    const handleSave = useCallback(
        (value: GQL.IDmsBewirtungInput) => run(() => saveBewirtung(document.key, value)),
        [document.key, run, saveBewirtung],
    );

    return (
        <div className="flex flex-col gap-3">
            <h3 className={QUESTION_CLASS}>
                <FormattedMessage id="app.dms.sortAssistant.bewirtungQuestion" />
            </h3>
            {!picked &&
                BEWIRTUNG_KINDS.map(({ kind, labelId }) => (
                    <AnswerButton
                        key={kind}
                        value={kind}
                        labelId={labelId}
                        isPending={isPending || isFetching}
                        onSelect={handleKind}
                    />
                ))}
            <Button className={ANSWER_CLASS} variant="outline" isDisabled={isPending} onPress={onAdvance}>
                <FormattedMessage id="app.dms.sortAssistant.noBewirtung" />
            </Button>
            <LaterButton isPending={isPending} onLater={onLater} />
            {picked?.failed && (
                <p role="alert" className="text-sm text-warning">
                    <FormattedMessage id="app.dms.bewirtung.aiLoadFailed" />
                </p>
            )}
            {prefill && <BewirtungForm initialValue={prefill} onSave={handleSave} isPending={isPending} />}
        </div>
    );
};

export const BewirtungPanel = memo(BewirtungPanelImpl);
